import { useSharedCheckoutContext } from '@root/components/checkout/context';
import { useProtectionPlanInfo } from '@root/components/checkout/hooks/use_protection_plan_info';
import { useProtectionTaxRate } from '@root/components/checkout/hooks/use_protection_tax_rate';
import { useStoragePlanInfo } from '@root/components/checkout/hooks/use_storage_plan_info';
import {
  resolvedTaxPercent,
  useStorageTaxRate,
} from '@root/components/checkout/hooks/use_storage_tax_rate';

import { ProtectionPlanNaming } from '@utils/protection_plan';

import {
  DEFAULT_RESERVATION_SUMMARY_PROPS,
  type ReservationSummaryProps,
} from './reservation_summary_props';

export const useReservationSummaryProps = (
  naming: ProtectionPlanNaming = 'classic',
): ReservationSummaryProps => {
  const {
    flowState: {
      values: { zip, address },
    },
  } = useSharedCheckoutContext();
  const storageTaxRate = useStorageTaxRate();
  const protectionTaxRate = useProtectionTaxRate();
  const storagePlanDetail = useStoragePlanInfo();
  const protectionPlanDetail = useProtectionPlanInfo(naming);

  if (!storagePlanDetail) return DEFAULT_RESERVATION_SUMMARY_PROPS;

  const storagePlanAmount = storagePlanDetail.amount;
  const protectionPlanAmount = protectionPlanDetail.amount;
  const taxZip = Boolean(address?.zip?.trim() || zip);
  const storageTaxPercent = resolvedTaxPercent(storageTaxRate, taxZip);
  const protectionTaxPercent = resolvedTaxPercent(protectionTaxRate, taxZip);
  const taxesReady = storageTaxPercent != null && protectionTaxPercent != null;
  const summaryLoaded = taxesReady;

  const storagePlanTax = taxesReady
    ? storagePlanAmount * (storageTaxPercent / 100)
    : 0;
  const protectionPlanTax = taxesReady
    ? protectionPlanAmount * (protectionTaxPercent / 100)
    : 0;

  const monthlyTotalAmount = storagePlanAmount + protectionPlanAmount;

  return {
    summaryLoaded,
    taxError: undefined,
    storagePlanLabel: storagePlanDetail.label,
    protectionPlanAmount,
    protectionPlanLabel: protectionPlanDetail.label,
    storagePlanAmount,
    monthlyTotalAmount,
    storagePlanTax,
    protectionPlanTax,
  };
};
